import { CheckCircleTwoTone, DownloadOutlined, InfoCircleTwoTone } from '@ant-design5/icons';
import { Divider } from 'antd5';
import React, { type Dispatch, type SetStateAction, useEffect, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { date, status, type, vendor } from 'components/data/inspections';
import { Button } from 'components/ui';
import {
  type ProcessInspectionMetadataFileMutationResult,
  useGetExistingInspectionsForSummaryValidationQuery,
} from 'types/atlas-graphql';
import { STANDARD_COLUMN_SCROLL_Y } from 'utils/constants';

import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { Steps } from '../UploadInspectionDataModal';
import {
  AdditionalInfoText,
  ButtonContainer,
  ValidationTable,
} from '../UploadInspectionDataModal.style';

export type ValidationProps = ProcessInspectionMetadataFileMutationResult['data'] & {
  fileName: string;
  setStep: Dispatch<SetStateAction<Steps>>;
  rollback: {
    update: (toastMessage?: string) => Promise<void>;
    loading: boolean;
  };
  existingInspectionIds?: string[];
};

export const Validation: React.FC<ValidationProps> = ({
  processInspectionMetadataFile,
  fileName,
  setStep,
  rollback,
  existingInspectionIds,
}) => {
  const theme = useTheme();
  const { update: _handleRollback, loading: rollbackLoading } = rollback;
  const { setAlertState, setPendingStep, setErroredUploadSteps } =
    useUploadInspectionDataModalContext();

  const { inspections, errorFileUrl, failureCount } = processInspectionMetadataFile!;
  const inspectionCount = inspections.length;

  const { data: existingInspectionData } = useGetExistingInspectionsForSummaryValidationQuery({
    variables: {
      filterBy: [{ key: 'id', values: existingInspectionIds ?? [] }],
    },
    skip: !existingInspectionIds?.length,
  });

  const setAlertStateWarning = () => {
    setAlertState({
      alertTitle: `${failureCount} row${failureCount === 1 ? '' : 's'} in ${fileName} contained invalid or incomplete data.`,
      alertType: 'warning',
      alertDescription: 'Be aware that your inspections will contain incomplete or malformed data.',
    });
  };

  const handleContinue = () => {
    const nextStep = Steps.DetailsUpload;
    if (isValid) {
      setStep(nextStep);
    } else {
      setPendingStep(nextStep);
      setAlertStateWarning();
    }
  };

  const handleRollback = async (toastMessage?: string) => {
    await _handleRollback(toastMessage);

    setStep(Steps.SummaryUpload);
    setErroredUploadSteps(uploadErrors => ({ ...uploadErrors, summary: false }));
    setAlertState(null);
  };

  const isValid = failureCount === 0 && inspectionCount > 0;

  const { columns, rows } = useMemo(() => {
    if (existingInspectionData?.inspections?.items?.length) {
      const existingInspections = existingInspectionData.inspections.items;
      return {
        columns: [
          {
            key: 'isNew',
            title: '',
            dataIndex: 'isNew',
            render: (value: boolean) =>
              value ? (
                <span>
                  <CheckCircleTwoTone twoToneColor={theme.green} /> created
                </span>
              ) : (
                <span>
                  <InfoCircleTwoTone twoToneColor={theme.lightBlue} /> existing
                </span>
              ),
          },
          date,
          type,
          vendor,
          status,
        ],
        rows: [
          ...inspections.map(i => ({ isNew: true, ...i })),
          ...existingInspections.map(i => ({ isNew: false, ...i })),
        ],
      };
    }

    return { columns: [date, type, vendor, status], rows: inspections };
  }, [inspections, existingInspectionData, theme]);

  //TODO new message to use in case user decides ti go to other pages 'Be aware that your inspections will contain incomplete or malformed data.'
  useEffect(() => {
    if (!isValid) {
      setAlertState({
        alertTitle: `${failureCount} row${failureCount === 1 ? '' : 's'} in ${fileName} contained invalid or incomplete data.`,
        alertType: 'error',
        alertDescription:
          'Please use the button below to download the error file. Then complete the missing data and upload again.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  return (
    <>
      {!isValid && (
        <>
          <Button
            data-testid="download-error-file-btn"
            icon={<DownloadOutlined />}
            href={errorFileUrl}
          >
            Download Error File
          </Button>
          <AdditionalInfoText>
            Note: Horizon requires the inspection details to be formatted in a way that is
            compatible with our system. For more information on configured inspection details
            uploads, please visit our{' '}
            <a
              href="https://guide.skyspecs.com/horizon/-MPobGVTLxgvA9kxtck7/"
              target="_blank"
              rel="noopener noreferrer"
            >
              User Guide
            </a>
            .
          </AdditionalInfoText>
          <Divider />
        </>
      )}
      <p>
        {inspectionCount ?? 0} Inspection{inspectionCount === 1 ? '' : 's'} successfully created.{' '}
        {existingInspectionIds?.length
          ? `${existingInspectionIds.length} existing Inspections also ready for details.`
          : ''}
      </p>

      <ValidationTable columns={columns} dataSource={rows} scroll={STANDARD_COLUMN_SCROLL_Y} />
      <ButtonContainer>
        {isValid ? (
          <>
            <Button
              type="primary"
              danger={true}
              onClick={() => handleRollback(undefined)}
              loading={rollbackLoading}
            >
              Roll Back Changes
            </Button>
            <Button type="primary" onClick={handleContinue}>
              Continue to Details
            </Button>
          </>
        ) : inspectionCount > 0 ? (
          <>
            <Button
              type="primary"
              danger={true}
              onClick={() =>
                handleRollback(
                  'Created Inspections rolled back. Please reupload with corrected data for errored Inspections.'
                )
              }
              loading={rollbackLoading}
            >
              Roll Back & Reupload
            </Button>
            <Button type="primary" onClick={handleContinue}>
              Ignore Errors & Continue
            </Button>
          </>
        ) : (
          <>
            <Button
              type="primary"
              danger={true}
              onClick={() => handleRollback(undefined)}
              loading={rollbackLoading}
            >
              Reupload
            </Button>
            {!!existingInspectionIds?.length && (
              <Button type="primary" onClick={handleContinue}>
                Ignore Errors & Continue
              </Button>
            )}
          </>
        )}
      </ButtonContainer>
    </>
  );
};
